import { ROUTE_ACTIVITY_EVENT_TYPE, ROUTE_SEQUENCE_TYPE } from 'settings/constants/routeActivity'
import { formatDateStringToFE } from 'utils/date'

import type { RouteActivityActor, RouteActivityRowApi } from 'types/api/work-order/routeActivity'
import type ActivityTreeBuilder from 'utils/activity/ActivityTreeBuilder'

// Injects the route-slot activity feed into the WO activity tree, phrased as what happened to the
// stop: "Stop assigned to route 123 at sequence #1 · every Thursday". SEQUENCE is position noise,
// hidden unless the caller opts in.

const { ASSIGN, UNASSIGN, SEQUENCE } = ROUTE_ACTIVITY_EVENT_TYPE

// Every row renders the same way apart from its key, change type, and the two values.
const STOP_ROW = { field: 'Stop', renderRawValue: true }

const ROW_KINDS = {
  [ASSIGN]: { fieldKey: 'routeAssigned', changeType: 'Assigned' },
  [UNASSIGN]: { fieldKey: 'routeUnassigned', changeType: 'Unassigned' },
  [SEQUENCE]: { fieldKey: 'routeSequence', changeType: 'Moved' },
}

const DAY_LABELS: Record<string, string> = {
  MONDAY: 'Monday',
  TUESDAY: 'Tuesday',
  WEDNESDAY: 'Wednesday',
  THURSDAY: 'Thursday',
  FRIDAY: 'Friday',
  SATURDAY: 'Saturday',
  SUNDAY: 'Sunday',
}

const dayLabel = (day?: string | null): string => (day ? DAY_LABELS[day] || day : '')

// MASTER rows are recurring (day-of-week → "every Thursday"); DAILY rows are one date.
const whenLabel = (row: RouteActivityRowApi): string => {
  if (row.sequenceType === ROUTE_SEQUENCE_TYPE.DAILY) return formatDateStringToFE(row.serviceDate || '')
  const day = dayLabel(row.dayOfWeek)
  return day ? `every ${day}` : ''
}

const suffix = (row: RouteActivityRowApi): string => {
  const when = whenLabel(row)
  const wo = row.workOrderNumber != null && row.workOrderNumber !== '' ? `WO #${row.workOrderNumber}` : ''
  const parts = [when, wo].filter(Boolean)
  return parts.length ? ` · ${parts.join(' · ')}` : ''
}

const routeName = (row: RouteActivityRowApi): string => row.route?.name || ''

const routePhrase = (row: RouteActivityRowApi): string => {
  const name = routeName(row)
  return name ? `route ${name}` : 'a route'
}

const atSequence = (row: RouteActivityRowApi): string => (row.sequence != null ? ` at sequence #${row.sequence}` : '')

// The feed records the post-op position only, so "moved from #1 to #20" needs the prior position:
// the most recent older row on the same route and the same day / service date.
const partitionKey = (row: RouteActivityRowApi): string => {
  const route = row.routeId || row.route?.id || ''
  const slice = row.sequenceType === ROUTE_SEQUENCE_TYPE.DAILY ? row.serviceDate || '' : row.dayOfWeek || ''
  return `${row.sequenceType}|${route}|${slice}`
}

const previousSequence = (rows: RouteActivityRowApi[], index: number): number | null => {
  const key = partitionKey(rows[index])
  for (let i = index + 1; i < rows.length; i += 1) {
    const candidate = rows[i]
    if (candidate && partitionKey(candidate) === key && candidate.sequence != null) return candidate.sequence
  }
  return null
}

// createdByUser is crm-core VersionUserResponse; nulls normalized to undefined for extractUserMeta's UserRef.
const toUserRef = (actor?: RouteActivityActor | null) =>
  actor
    ? { firstName: actor.firstName ?? undefined, lastName: actor.lastName ?? undefined, username: actor.username ?? undefined }
    : undefined

const toVersionMeta = (row: RouteActivityRowApi) => ({
  updatedAt: row.occurredAt,
  updatedByUser: toUserRef(row.createdByUser),
  lastModifiedBy: row.createdBy || undefined,
})

export type RouteActivityBuilderPayload = {
  rows?: RouteActivityRowApi[]
  includeSequenceChanges?: boolean
}

export default function routeActivityBuilder(builder: ActivityTreeBuilder, payload?: unknown): void {
  const { rows = [], includeSequenceChanges = false } = (payload as RouteActivityBuilderPayload) || {}
  if (!Array.isArray(rows) || rows.length === 0) return

  rows.forEach((row, index) => {
    if (!row || !row.eventType) return
    if (row.eventType === SEQUENCE && !includeSequenceChanges) return

    // Namespaced string rev keeps routing events out of the Envers rev space (no accidental
    // batch merges); buildTree orders them by occurredAt, so the key only needs to be unique.
    const rev = `route-${row.occurredAt || 'na'}-${index}`
    const version = toVersionMeta(row)

    switch (row.eventType) {
      case ASSIGN:
        builder.addCustomChange(rev, version, {
          ...STOP_ROW,
          ...ROW_KINDS[ASSIGN],
          oldValue: undefined,
          newValue: `to ${routePhrase(row)}${atSequence(row)}${suffix(row)}`,
        })
        break
      case UNASSIGN:
        builder.addCustomChange(rev, version, {
          ...STOP_ROW,
          ...ROW_KINDS[UNASSIGN],
          oldValue: `from ${routePhrase(row)}${atSequence(row)}${suffix(row)}`,
          newValue: undefined,
        })
        break
      case SEQUENCE: {
        // The cell supplies the "from"/"to" words; an empty oldValue drops the "from" clause when
        // the feed has no earlier position on this route and day (nothing to compare against).
        const previous = previousSequence(rows, index)
        const to = row.sequence != null ? `sequence #${row.sequence}` : 'a new sequence'
        builder.addCustomChange(rev, version, {
          ...STOP_ROW,
          ...ROW_KINDS[SEQUENCE],
          oldValue: previous != null ? `sequence #${previous}` : '',
          newValue: `${to} on ${routePhrase(row)}${suffix(row)}`,
        })
        break
      }
      default:
        break
    }
  })
}
